import { useEffect } from 'react';
import { DataSecurityTct, TransactionType } from '@dealstack/shared/lib/generated';
import {
	isSecurityIssuedInCurrency,
	isSecurityIssuedInUnits,
} from '@dealstack/frontend/shared/cap.ts';
import { HIDE_UNITS_TYPES } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility.ts';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number.ts';
import { resolveTransactionTypeDerivedFields } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/transactionTypeDerivedFields';

interface UseTransactionPartyDefaultsParams {
	transactionType?: TransactionType | null;
	selectedSecurity?: DataSecurityTct;
	issuerId?: string | null;
	getValues: (name: string) => string | number | null;
	setValue: (name: string, value: string | number | null) => void;
	clearErrors: (name: unknown) => void;
	fields: {
		buyer: string;
		seller: string;
		price: string;
		units: string;
	};
}

export const useTransactionPartyDefaults = ({
	transactionType,
	selectedSecurity,
	issuerId,
	getValues,
	setValue,
	clearErrors,
	fields,
}: UseTransactionPartyDefaultsParams) => {
	const { buyer: buyerField, seller: sellerField, price: priceField, units: unitsField } = fields;

	//seller/buyer default value
	useEffect(() => {
		if (!transactionType) {
			return;
		}

		const currentValues = resolveTransactionTypeDerivedFields({
			transactionType,
			issuerId,
			securityIssuedIn: selectedSecurity?.issuedIn,
			values: {
				sourceId: getValues(sellerField) as string | null,
				destinationId: getValues(buyerField) as string | null,
				cost: getValues(priceField) as number | null,
				units: getValues(unitsField) as number | null,
			},
		});

		if (currentValues.sourceId !== getValues(sellerField)) {
			setValue(sellerField, currentValues.sourceId ?? null);
			clearErrors(sellerField);
		}

		if (currentValues.destinationId !== getValues(buyerField)) {
			setValue(buyerField, currentValues.destinationId ?? null);
			clearErrors(buyerField);
		}

		if (currentValues.cost !== undefined && currentValues.cost !== getValues(priceField)) {
			setValue(priceField, currentValues.cost ?? null);
		}
	}, [transactionType, issuerId, selectedSecurity]);

	//units default value
	useEffect(() => {
		// set units to 0 if transaction type is in HIDE_UNITS_TYPES
		if (HIDE_UNITS_TYPES.includes(transactionType as TransactionType)) {
			setValue(unitsField, 0);
			return;
		}

		// if issued in currency then set units to 1
		if (selectedSecurity && isSecurityIssuedInCurrency(selectedSecurity.issuedIn)) {
			setValue(unitsField, 1);
		}
	}, [transactionType, selectedSecurity]);

	const unitsDecimalPoints = extractDecimalPrecisionFromUnitDenominator(
		selectedSecurity?.unitDenominator
	);
	// this makes sure that the form value is always correctly formatted with the precision of the selected security
	useEffect(() => {
		if (selectedSecurity && isSecurityIssuedInUnits(selectedSecurity.issuedIn)) {
			const currentUnits = getValues(unitsField) as number;
			const formattedValue = Number(currentUnits?.toFixed(unitsDecimalPoints));

			setValue(unitsField, formattedValue);
		}
	}, [unitsDecimalPoints]);

	return {
		unitsDecimalPoints,
	};
};
